import React, { useEffect, useState } from 'react';
import { pastPerformanceApi } from '../../services/api';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  BriefcaseIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  CheckBadgeIcon,
} from 'components/icons';
import Modal from '../../components/ui/Modal';
import PageHeader from '../../components/ui/PageHeader';

interface PastPerformanceProject {
  id: string;
  title: string;
  customer: string;
  customerType: string;
  contractNumber?: string;
  contractType?: string;
  role: string;
  startDate?: string;
  endDate?: string;
  isOngoing: boolean;
  contractValue?: string;
  naicsCodes: string[];
  setAside?: string;
  classification?: string;
  scopeSummary: string;
  technicalNarrative: string;
  keyOutcomes: string[];
  challenges?: string;
  cparsRating?: string;
  awards: string[];
  pocName?: string;
  pocTitle?: string;
  pocPhone?: string;
  pocEmail?: string;
  pocPermission: boolean;
  tags: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface FormState {
  title: string;
  customer: string;
  customerType: string;
  contractNumber: string;
  contractType: string;
  role: string;
  startDate: string;
  endDate: string;
  isOngoing: boolean;
  contractValue: string;
  naicsCodes: string;
  setAside: string;
  classification: string;
  scopeSummary: string;
  technicalNarrative: string;
  keyOutcomes: string;
  cparsRating: string;
  pocName: string;
  pocTitle: string;
  pocPhone: string;
  pocEmail: string;
  pocPermission: boolean;
  tags: string;
}

const EMPTY_FORM: FormState = {
  title: '',
  customer: '',
  customerType: 'federal',
  contractNumber: '',
  contractType: '',
  role: 'prime',
  startDate: '',
  endDate: '',
  isOngoing: false,
  contractValue: '',
  naicsCodes: '',
  setAside: '',
  classification: '',
  scopeSummary: '',
  technicalNarrative: '',
  keyOutcomes: '',
  cparsRating: '',
  pocName: '',
  pocTitle: '',
  pocPhone: '',
  pocEmail: '',
  pocPermission: false,
  tags: '',
};

const PastPerformance: React.FC = () => {
  const [items, setItems] = useState<PastPerformanceProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await pastPerformanceApi.list();
      setItems(res.data?.data?.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const startEdit = (p: PastPerformanceProject) => {
    setEditingId(p.id);
    setForm({
      title: p.title,
      customer: p.customer,
      customerType: p.customerType,
      contractNumber: p.contractNumber || '',
      contractType: p.contractType || '',
      role: p.role,
      startDate: p.startDate ? p.startDate.split('T')[0] : '',
      endDate: p.endDate ? p.endDate.split('T')[0] : '',
      isOngoing: p.isOngoing,
      contractValue: p.contractValue || '',
      naicsCodes: p.naicsCodes.join(', '),
      setAside: p.setAside || '',
      classification: p.classification || '',
      scopeSummary: p.scopeSummary,
      technicalNarrative: p.technicalNarrative,
      keyOutcomes: p.keyOutcomes.join('\n'),
      cparsRating: p.cparsRating || '',
      pocName: p.pocName || '',
      pocTitle: p.pocTitle || '',
      pocPhone: p.pocPhone || '',
      pocEmail: p.pocEmail || '',
      pocPermission: p.pocPermission,
      tags: p.tags.join(', '),
    });
    setShowForm(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: form.title,
        customer: form.customer,
        customerType: form.customerType,
        contractNumber: form.contractNumber || null,
        contractType: form.contractType || null,
        role: form.role,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        isOngoing: form.isOngoing,
        contractValue: form.contractValue ? parseFloat(form.contractValue) : null,
        naicsCodes: form.naicsCodes.split(',').map((s) => s.trim()).filter(Boolean),
        setAside: form.setAside || null,
        classification: form.classification || null,
        scopeSummary: form.scopeSummary,
        technicalNarrative: form.technicalNarrative,
        keyOutcomes: form.keyOutcomes.split('\n').map((s) => s.trim()).filter(Boolean),
        cparsRating: form.cparsRating || null,
        pocName: form.pocName || null,
        pocTitle: form.pocTitle || null,
        pocPhone: form.pocPhone || null,
        pocEmail: form.pocEmail || null,
        pocPermission: form.pocPermission,
        tags: form.tags.split(',').map((s) => s.trim()).filter(Boolean),
      };
      if (editingId) {
        await pastPerformanceApi.update(editingId, payload);
      } else {
        await pastPerformanceApi.create(payload);
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this past performance record? This cannot be undone.')) return;
    try {
      await pastPerformanceApi.remove(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Delete failed');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <PageHeader
        title="Past Performance Library"
        subtitle="Reusable contract records. The proposal writer pulls from these when generating past performance and technical narratives — no more re-typing the same content for every bid."
      />
        <button
          onClick={startCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-gradient-to-r from-brand-600 to-purple-600 hover:from-brand-500 hover:to-purple-500"
        >
          <PlusIcon className="w-5 h-5" /> Add Project
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-content-muted text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-surface rounded-lg border border-border p-12 text-center">
          <BriefcaseIcon className="w-12 h-12 mx-auto text-content-subtle mb-4" />
          <h3 className="text-title text-content mb-2">No past performance records yet</h3>
          <p className="text-sm text-content-muted mb-6 max-w-md mx-auto">
            Add at least one record so the proposal writer can pull real content into your proposals
            instead of writing <code className="bg-surface-sunk px-1 rounded">[USER: provide X]</code>{' '}
            placeholders.
          </p>
          <button
            onClick={startCreate}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-gradient-to-r from-brand-600 to-purple-600 hover:from-brand-500 hover:to-purple-500"
          >
            <PlusIcon className="w-5 h-5" /> Add Your First Project
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div
              key={p.id}
              className={`bg-surface rounded-lg border p-4 ${p.isActive ? 'border-border' : 'border-border opacity-60'}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-content">{p.title}</h3>
                  <div className="text-sm text-content-muted mt-1">
                    {p.customer} <span className="text-content-subtle">·</span>{' '}
                    <span className="capitalize">{p.customerType}</span>
                    {p.contractNumber && (
                      <>
                        {' '}
                        <span className="text-content-subtle">·</span> {p.contractNumber}
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-2 text-xs text-content-muted">
                    {p.contractType && (
                      <span className="inline-flex items-center gap-1">
                        <BriefcaseIcon className="w-4 h-4" /> {p.contractType}
                      </span>
                    )}
                    {p.contractValue && (
                      <span className="inline-flex items-center gap-1">
                        <CurrencyDollarIcon className="w-4 h-4" />$
                        {Number(p.contractValue).toLocaleString()}
                      </span>
                    )}
                    {(p.startDate || p.endDate) && (
                      <span className="inline-flex items-center gap-1">
                        <CalendarIcon className="w-4 h-4" />
                        {p.startDate ? p.startDate.split('T')[0] : '?'} —{' '}
                        {p.isOngoing ? 'ongoing' : p.endDate ? p.endDate.split('T')[0] : '?'}
                      </span>
                    )}
                    {p.cparsRating && (
                      <span className="inline-flex items-center gap-1 text-green-700">
                        <CheckBadgeIcon className="w-4 h-4" /> {p.cparsRating}
                      </span>
                    )}
                    {p.naicsCodes.length > 0 && (
                      <span>NAICS: {p.naicsCodes.join(', ')}</span>
                    )}
                  </div>
                  <p className="text-sm text-content mt-2">{p.scopeSummary}</p>
                  {p.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {p.tags.map((t) => (
                        <span
                          key={t}
                          className="inline-block text-xs px-2 py-0.5 rounded-full bg-surface-sunk text-content"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => startEdit(p)}
                    className="p-2 text-content-subtle hover:text-content-muted"
                    title="Edit"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(p.id)}
                    className="p-2 text-content-subtle hover:text-red-600"
                    title="Delete"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal form */}
      {/* Overlay dismissal is off — this is a long data-entry form and a stray
          backdrop click would discard everything typed. Escape still closes. */}
      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        dismissOnOverlayClick={false}
        title={`${editingId ? 'Edit' : 'Add'} Past Performance Project`}
        size="xl"
        footer={
          <>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" form="past-performance-form" disabled={saving} className="btn-primary">
              {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Project'}
            </button>
          </>
        }
      >
            {/* Submit lives in the modal footer, outside this element, so it
                targets the form by id. */}
            <form id="past-performance-form" onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Project Title *" required>
                  <input
                    type="text"
                    required
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="input"
                    placeholder="HVAC System Replacement at Naval Station Norfolk"
                  />
                </Field>
                <Field label="Customer *" required>
                  <input
                    type="text"
                    required
                    value={form.customer}
                    onChange={(e) => setForm({ ...form, customer: e.target.value })}
                    className="input"
                    placeholder="Department of the Navy"
                  />
                </Field>
                <Field label="Customer Type">
                  <select
                    value={form.customerType}
                    onChange={(e) => setForm({ ...form, customerType: e.target.value })}
                    className="input"
                  >
                    <option value="federal">Federal</option>
                    <option value="state">State</option>
                    <option value="local">Local</option>
                    <option value="commercial">Commercial</option>
                    <option value="tribal">Tribal</option>
                  </select>
                </Field>
                <Field label="Role">
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="input"
                  >
                    <option value="prime">Prime</option>
                    <option value="subcontractor">Subcontractor</option>
                    <option value="teaming_partner">Teaming Partner</option>
                  </select>
                </Field>
                <Field label="Contract Number">
                  <input
                    type="text"
                    value={form.contractNumber}
                    onChange={(e) => setForm({ ...form, contractNumber: e.target.value })}
                    className="input"
                    placeholder="N00038-22-D-0001"
                  />
                </Field>
                <Field label="Contract Type">
                  <select
                    value={form.contractType}
                    onChange={(e) => setForm({ ...form, contractType: e.target.value })}
                    className="input"
                  >
                    <option value="">Select…</option>
                    <option value="FFP">Firm-Fixed-Price (FFP)</option>
                    <option value="T&M">Time & Materials (T&M)</option>
                    <option value="COST_PLUS">Cost-Plus</option>
                    <option value="IDIQ">IDIQ</option>
                    <option value="BPA">BPA</option>
                    <option value="SIMPLIFIED">Simplified Acquisition</option>
                  </select>
                </Field>
                <Field label="Start Date">
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                    className="input"
                  />
                </Field>
                <Field label="End Date">
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={form.endDate}
                      onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                      className="input flex-1"
                      disabled={form.isOngoing}
                    />
                    <label className="text-sm text-content-muted whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={form.isOngoing}
                        onChange={(e) => setForm({ ...form, isOngoing: e.target.checked })}
                        className="mr-1"
                      />
                      Ongoing
                    </label>
                  </div>
                </Field>
                <Field label="Contract Value ($)">
                  <input
                    type="number"
                    step="0.01"
                    value={form.contractValue}
                    onChange={(e) => setForm({ ...form, contractValue: e.target.value })}
                    className="input"
                    placeholder="1850000"
                  />
                </Field>
                <Field label="NAICS Codes">
                  <input
                    type="text"
                    value={form.naicsCodes}
                    onChange={(e) => setForm({ ...form, naicsCodes: e.target.value })}
                    className="input"
                    placeholder="238220, 238210"
                  />
                </Field>
                <Field label="Set-Aside">
                  <input
                    type="text"
                    value={form.setAside}
                    onChange={(e) => setForm({ ...form, setAside: e.target.value })}
                    className="input"
                    placeholder="SDVOSB"
                  />
                </Field>
                <Field label="CPARS Rating">
                  <select
                    value={form.cparsRating}
                    onChange={(e) => setForm({ ...form, cparsRating: e.target.value })}
                    className="input"
                  >
                    <option value="">N/A</option>
                    <option value="Exceptional">Exceptional</option>
                    <option value="Very Good">Very Good</option>
                    <option value="Satisfactory">Satisfactory</option>
                    <option value="Marginal">Marginal</option>
                    <option value="Unsatisfactory">Unsatisfactory</option>
                  </select>
                </Field>
              </div>

              <Field label="Scope Summary * (2-3 sentences)" required>
                <textarea
                  required
                  rows={2}
                  value={form.scopeSummary}
                  onChange={(e) => setForm({ ...form, scopeSummary: e.target.value })}
                  className="input"
                  placeholder="Replaced 24 commercial HVAC units across three buildings under a phased plan that maintained continuous operations."
                />
              </Field>

              <Field label="Technical Narrative * (full paragraph used in proposals)" required>
                <textarea
                  required
                  rows={6}
                  value={form.technicalNarrative}
                  onChange={(e) => setForm({ ...form, technicalNarrative: e.target.value })}
                  className="input"
                  placeholder="Acme HVAC Inc executed a 14-month FFP contract to replace 24 aging commercial rooftop HVAC units…"
                />
              </Field>

              <Field label="Key Outcomes (one per line)">
                <textarea
                  rows={3}
                  value={form.keyOutcomes}
                  onChange={(e) => setForm({ ...form, keyOutcomes: e.target.value })}
                  className="input"
                  placeholder="Delivered 11 days ahead of schedule&#10;22% efficiency improvement&#10;Zero safety incidents"
                />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="POC Name">
                  <input
                    type="text"
                    value={form.pocName}
                    onChange={(e) => setForm({ ...form, pocName: e.target.value })}
                    className="input"
                  />
                </Field>
                <Field label="POC Title">
                  <input
                    type="text"
                    value={form.pocTitle}
                    onChange={(e) => setForm({ ...form, pocTitle: e.target.value })}
                    className="input"
                  />
                </Field>
                <Field label="POC Phone">
                  <input
                    type="text"
                    value={form.pocPhone}
                    onChange={(e) => setForm({ ...form, pocPhone: e.target.value })}
                    className="input"
                  />
                </Field>
                <Field label="POC Email">
                  <input
                    type="email"
                    value={form.pocEmail}
                    onChange={(e) => setForm({ ...form, pocEmail: e.target.value })}
                    className="input"
                  />
                </Field>
              </div>

              <Field label="Tags (comma-separated)">
                <input
                  type="text"
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  className="input"
                  placeholder="HVAC, phased rollout, federal, DoD"
                />
              </Field>

              <label className="flex items-center gap-2 text-sm text-content">
                <input
                  type="checkbox"
                  checked={form.pocPermission}
                  onChange={(e) => setForm({ ...form, pocPermission: e.target.checked })}
                />
                POC has been notified they may be contacted as a reference
              </label>

            </form>
      </Modal>
    </div>
  );
};

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <label className="block text-sm font-medium text-content mb-1">{label}</label>
    {children}
  </div>
);

export default PastPerformance;
